import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, ImagePlus, Loader2, MessageSquareText, Replace, Search, Sparkles, X, PackageSearch, ListPlus, Camera, BookOpen } from 'lucide-react';
import ItemCard, { ItemCardSkeleton } from '../components/items/ItemCard';
import ItemImage from '../components/items/ItemImage';
import ItemDetailSheet from '../components/items/ItemDetailSheet';
import ShortlistPicker from '../components/shortlists/ShortlistPicker';
import { EmptyState, ErrorState, PageHeader } from '../components/common/States';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Hint } from '../components/ui/tooltip';
import useDebounce from '../hooks/useDebounce';
import { useCapabilities, useMeta } from '../hooks/queries';
import FilterPanel from '../components/explore/FilterPanel';
import { FILTER_KEYS, countFilters, filterChips, parseFilters, removeChip } from '../lib/filters';
import { api, errorMessage, toSearchParams } from '../lib/api';
import { FOCUS_OPTIONS } from '../lib/taxonomy';
import { fmtInt, fmtPrice } from '../lib/format';
import { cn } from '../lib/utils';
import WaitingModal from '../components/common/WaitingModal';
import { SUBSTITUTE_TOUR_EXAMPLE, startTour, tourSeen } from '../lib/tour';

const MAX_PICK = 12;

const EXAMPLE_REQUESTS = ['Flowerpot VP1', 'PH 5 pendant', 'Tolomeo', 'IC Lights', 'Panthella', 'Wire chair'];
const EXAMPLE_DESCRIPTIONS = [
  'Flos IC Lights pendant in brass, about 40 cm, 6 pieces for a restaurant',
  'Black outdoor wall light for a hotel terrace, IP44, 10 pieces',
  'Big rattan pendant for a beach bar, natural look',
  'Green velvet lounge chair, mid-century style',
];
const SOURCES = [
  { value: 'photo', label: 'From a photo', icon: Camera, hint: 'A picture the client sent, a screenshot, a mood board' },
  { value: 'describe', label: 'Describe it', icon: MessageSquareText, hint: 'Any product, even one we do not sell' },
  { value: 'catalog', label: 'From our catalogue', icon: BookOpen, hint: 'A product from the PIM' },
];

/** Drop, paste or pick the client's photo. */
function PhotoInput({ file, onFile }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);
  useEffect(() => {
    const onPaste = (e) => {
      const f = [...(e.clipboardData?.files || [])].find((x) => x.type.startsWith('image/'));
      if (f) onFile(f);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onFile]);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = [...(e.dataTransfer?.files || [])].find((x) => x.type.startsWith('image/'));
        if (f) onFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
      className={cn(
        'flex cursor-pointer items-center gap-4 rounded-lg border border-dashed bg-card p-4 transition hover:border-foreground/30',
        drag && 'border-foreground/50 bg-accent'
      )}
    >
      {preview ? (
        <img src={preview} alt="Requested product" className="h-20 w-20 shrink-0 rounded-md border bg-white object-contain p-1" />
      ) : (
        <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border bg-muted/50 text-muted-foreground">
          <ImagePlus className="h-7 w-7" />
        </span>
      )}
      <div className="min-w-0 text-sm">
        <p className="font-medium">{file ? 'Use another photo' : 'Drop the client’s photo here'}</p>
        <p className="text-xs text-muted-foreground">Or click to choose a file, or paste a screenshot (Ctrl/Cmd + V). JPG, PNG or WebP, max 8 MB.</p>
        {file && <p className="mt-1 truncate text-xs text-muted-foreground">{file.name}</p>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
      />
    </div>
  );
}

/** Free text: any product, even one we do not sell. */
function DescribeInput({ initial, onSubmit, loading }) {
  const [text, setText] = useState(initial || '');
  useEffect(() => setText(initial || ''), [initial]);
  const submit = () => text.trim().length >= 3 && onSubmit(text.trim());
  return (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          placeholder="What did the client ask for? Brand and model, or just what it looks like, size, colour, budget, number of pieces…"
          className="resize-none pr-28 text-[15px]"
        />
        <Button size="sm" className="absolute bottom-2 right-2 h-8 gap-1.5" onClick={submit} disabled={loading || text.trim().length < 3}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Find
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {EXAMPLE_DESCRIPTIONS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setText(t);
              onSubmit(t);
            }}
            className="rounded-full border bg-background px-2.5 py-1 text-xs text-muted-foreground transition hover:border-foreground/30 hover:text-foreground"
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

/** What Toxy understood from the photo or the description. */
function UnderstoodCard({ source, photoUrl }) {
  if (!source) return null;
  const facts = [
    source.type && source.type !== 'other' && ['Type', source.type],
    source.colour && ['Colour', source.colour],
    source.materials?.length > 0 && ['Material', source.materials.join(', ')],
    source.size_cm && ['Size', `about ${Math.round(source.size_cm)} cm`],
    source.estimated_price_eur && ['Price', `about ${fmtPrice(source.estimated_price_eur)}`],
    source.brand && ['Brand', source.brand],
    source.designer && ['Designer', source.designer],
    source.pieces_needed && ['Pieces', `${source.pieces_needed} needed`],
    source.outdoor && ['Use', 'Outdoors (IP44+)'],
  ].filter(Boolean);
  return (
    <div className="flex gap-3 p-3 lg:flex-col">
      {photoUrl ? (
        <img src={photoUrl} alt="" className="aspect-square w-24 shrink-0 rounded-lg border bg-white object-contain p-2 lg:w-full" />
      ) : (
        <span className="flex aspect-square w-24 shrink-0 items-center justify-center rounded-lg border bg-muted/40 text-muted-foreground lg:h-24 lg:w-full lg:aspect-auto">
          <MessageSquareText className="h-8 w-8" />
        </span>
      )}
      <div className="min-w-0 space-y-2">
        <div className="text-sm font-medium leading-snug">{source.name}</div>
        {facts.length > 0 && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
            {facts.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="truncate">{v}</dd>
              </div>
            ))}
          </dl>
        )}
        <p className="text-[11px] text-muted-foreground">
          {source.ai ? 'Read by AI. Matched on these details and on the look.' : 'Matched on the look and the words.'}
        </p>
      </div>
    </div>
  );
}

function CatalogTypeahead({ onPick, seed }) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  useEffect(() => {
    if (!seed) return;
    setText(seed.text);
    setOpen(true);
    inputRef.current?.focus();
  }, [seed]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = useDebounce(text.trim(), 250);
  const boxRef = useRef(null);
  const { data, isFetching, error } = useQuery({
    queryKey: ['catalog-search', q],
    queryFn: () => api.catalogSearch(q),
    enabled: q.length >= 2,
    placeholderData: keepPreviousData,
  });
  const results = q.length >= 2 ? data?.results || [] : [];

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    const onDoc = (e) => !boxRef.current?.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (r) => {
    onPick(r);
    setOpen(false);
    setText('');
  };

  return (
    <div ref={boxRef} className="relative">
      <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!results.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(results[active]);
          } else if (e.key === 'Escape') setOpen(false);
        }}
        placeholder="Which product did the client ask for? Search the whole catalogue…"
        className="h-12 w-full rounded-lg border border-input bg-background pl-12 pr-10 text-[15px] shadow-sm outline-none transition placeholder:text-muted-foreground/80 focus:border-foreground/30 focus:ring-4 focus:ring-ring/10"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {isFetching && <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      {open && q.length >= 2 && (
        <div className="absolute z-40 mt-1.5 max-h-[420px] w-full overflow-y-auto rounded-lg border bg-popover p-1 shadow-lg" role="listbox">
          {error && <p className="px-3 py-2 text-sm text-destructive">{errorMessage(error)}</p>}
          {!error && !results.length && !isFetching && <p className="px-3 py-3 text-sm text-muted-foreground">No catalogue product matches “{q}”.</p>}
          {results.map((r, i) => (
            <button
              key={r.odoo_id}
              type="button"
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(r)}
              className={cn('flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left', i === active && 'bg-accent')}
            >
              <ItemImage src={r.image_url} className="h-11 w-11 shrink-0 border p-0.5" rounded="rounded-md" iconClassName="h-4 w-4" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{r.name}</div>
                <div className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                  {r.brand} · #{r.odoo_id}
                  {r.is_problem && <Badge variant="danger">problem stock</Badge>}
                </div>
              </div>
              <div className="shrink-0 text-right text-xs tabular-nums">
                <div className="font-medium">{fmtPrice(r.price)}</div>
                <div className="text-muted-foreground">
                  {r.p_status ? `${r.p_status} · ` : ''}
                  {fmtInt(r.free_stock)} free
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-8 gap-1.5"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* clipboard blocked */
        }
      }}
    >
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {done ? 'Copied' : 'Copy'}
    </Button>
  );
}

export default function Substitute() {
  const [sp, setSp] = useSearchParams();
  const productId = sp.get('product');
  const focus = sp.get('focus') || 'balanced';
  const [bandPct, setBandPct] = useState(() => Math.round(Number(sp.get('band') || 0.5) * 100));
  const band = useDebounce(bandPct, 350) / 100;
  const [qtyText, setQtyText] = useState(() => sp.get('qty') || '');
  const qtyDebounced = useDebounce(qtyText, 400);
  const qty = /^\d+$/.test(qtyDebounced) && Number(qtyDebounced) > 0 ? Number(qtyDebounced) : undefined;
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [openId, setOpenId] = useState(null);
  const [pickedSource, setPickedSource] = useState(null);
  const [seed, setSeed] = useState(null);
  const caps = useCapabilities();
  const { data: meta } = useMeta();
  // same filters as Explore, kept in the URL
  const filters = useMemo(() => parseFilters(sp), [sp]);
  const setFilters = (f) => {
    const next = new URLSearchParams(sp);
    FILTER_KEYS.forEach((k) => next.delete(k));
    toSearchParams(f).forEach((v, k) => next.append(k, v));
    setSp(next);
  };
  const from = SOURCES.some((x) => x.value === sp.get('from')) ? sp.get('from') : productId ? 'catalog' : 'photo';
  const [photoFile, setPhotoFile] = useState(null);
  const photoUrl = useMemo(() => (photoFile ? URL.createObjectURL(photoFile) : null), [photoFile]);
  useEffect(() => () => photoUrl && URL.revokeObjectURL(photoUrl), [photoUrl]);
  const describeText = sp.get('q') || '';
  // guided tour (first visit, or ?tour=1): load an example request so there is something to show
  const wantsTour = sp.get('tour') === '1';
  useEffect(() => {
    if (wantsTour || tourSeen('substitute') || active) return;
    const next = new URLSearchParams(sp);
    next.set('tour', '1');
    setSp(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!wantsTour) return;
    if (!(from === 'describe' && describeText)) {
      const next = new URLSearchParams();
      next.set('from', 'describe');
      next.set('q', SUBSTITUTE_TOUR_EXAMPLE);
      next.set('tour', '1');
      setSp(next, { replace: true });
    }
  }, [wantsTour]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (k, v) => {
    const next = new URLSearchParams(sp);
    if (v === null || v === undefined || v === '') next.delete(k);
    else next.set(k, v);
    setSp(next, { replace: k === 'band' || k === 'qty' });
  };
  useEffect(() => {
    if (productId && Number(sp.get('band') || 0.5) !== band) setParam('band', band === 0.5 ? null : String(band));
  }, [band]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (productId && (sp.get('qty') || undefined) !== (qty ? String(qty) : undefined)) setParam('qty', qty ? String(qty) : null);
  }, [qty]); // eslint-disable-line react-hooks/exhaustive-deps

  const catalogQuery = useQuery({
    queryKey: ['substitutes', productId, focus, band, qty, filters],
    queryFn: () => api.substitutes(productId, focus, band, qty, filters),
    enabled: from === 'catalog' && !!productId,
    placeholderData: keepPreviousData,
  });
  const anyParams = { band, qty, ...(sp.get('focus') ? { focus } : {}), ...filters };
  const photoKey = photoFile ? `${photoFile.name}:${photoFile.size}:${photoFile.lastModified}` : null;
  const photoQuery = useQuery({
    queryKey: ['substitute-photo', photoKey, focus, band, qty, filters],
    queryFn: () => api.substitutePhoto(photoFile, anyParams),
    enabled: from === 'photo' && !!photoFile,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });
  const describeQuery = useQuery({
    queryKey: ['substitute-describe', describeText, focus, band, qty, filters],
    queryFn: () => api.substituteDescribe(describeText, anyParams),
    enabled: from === 'describe' && !!describeText,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
  });
  const subs = from === 'catalog' ? catalogQuery : from === 'photo' ? photoQuery : describeQuery;
  const active = from === 'catalog' ? !!productId : from === 'photo' ? !!photoFile : !!describeText;
  const source =
    from === 'catalog' ? catalogQuery.data?.source || (pickedSource?.odoo_id === Number(productId) ? pickedSource : null) : active ? subs.data?.source : null;
  const results = useMemo(() => (active ? subs.data?.results || [] : []), [subs.data, active]);
  useEffect(() => {
    if (!wantsTour || !results.length) return undefined;
    const t = setTimeout(() => {
      startTour('substitute');
      const next = new URLSearchParams(window.location.search);
      next.delete('tour');
      setSp(next, { replace: true });
    }, 600);
    return () => clearTimeout(t);
  }, [wantsTour, results.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const setFrom = (v) => {
    const next = new URLSearchParams(sp);
    next.set('from', v);
    if (v !== 'catalog') next.delete('product');
    setSp(next);
  };
  const clearSource = () => {
    if (from === 'catalog') setParam('product', null);
    else if (from === 'photo') setPhotoFile(null);
    else setParam('q', null);
  };
  const byId = useMemo(() => Object.fromEntries(results.map((r) => [r.odoo_product_id, r])), [results]);

  const curate = useMutation({
    mutationFn: () =>
      from === 'catalog'
        ? api.curate(productId, selected, note.trim())
        : api.curateAny(source, selected, note.trim(), from === 'photo' ? photoFile : null),
  });

  // reset selection when the requested product changes
  useEffect(() => {
    setSelected([]);
    curate.reset();
  }, [productId, from, photoKey, describeText]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (item) =>
    setSelected((s) => (s.includes(item.odoo_product_id) ? s.filter((x) => x !== item.odoo_product_id) : s.length >= MAX_PICK ? s : [...s, item.odoo_product_id]));

  const picks = curate.data?.picks || [];
  const pickIds = picks.map((p) => p.odoo_product_id);

  return (
    <div className="w-full px-3 pb-0 pt-4 sm:px-6 lg:pt-6">
      <WaitingModal active={curate.isPending || (active && subs.isFetching)} mode={curate.isPending ? 'curate' : from === 'photo' ? 'visual' : 'substitute'} />
      <PageHeader title="Substitute" description="A client asks for something: what do we already have in our problem stock that could work instead?" className="mb-4" />
      <div className="max-w-3xl space-y-3">
        <Tabs value={from} onValueChange={setFrom}>
          <TabsList className="h-9" data-tour="sub-sources">
            {SOURCES.map((x) => (
              <TabsTrigger key={x.value} value={x.value} className="gap-1.5 px-3 text-[13px]" title={x.hint}>
                <x.icon className="h-3.5 w-3.5" /> {x.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div data-tour="sub-input">
          {from === 'photo' && <PhotoInput file={photoFile} onFile={setPhotoFile} />}
          {from === 'describe' && <DescribeInput initial={describeText} onSubmit={(t) => setParam('q', t)} loading={describeQuery.isFetching} />}
          {from === 'catalog' && (
            <CatalogTypeahead
              seed={seed}
              onPick={(r) => {
                setPickedSource(r);
                setParam('product', String(r.odoo_id));
              }}
            />
          )}
        </div>
      </div>

      {from === 'catalog' && !productId && (
        <EmptyState icon={Replace} title="Pick the requested product" className="mt-8">
          Search any product from the catalogue — in stock or not, listed or not — and Toxy proposes problem stock that could replace it.
          <div className="mt-4 flex flex-wrap justify-center gap-1.5">
            <span className="w-full text-xs text-muted-foreground">Try a typical request:</span>
            {EXAMPLE_REQUESTS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setSeed({ text: t, n: Date.now() })}
                className="rounded-full border bg-background px-3 py-1 text-xs font-medium text-foreground transition hover:border-foreground/40"
              >
                {t}
              </button>
            ))}
          </div>
        </EmptyState>
      )}

      {from !== 'catalog' && !active && (
        <EmptyState icon={from === 'photo' ? Camera : MessageSquareText} title={from === 'photo' ? 'Start from a photo' : 'Describe what the client wants'} className="mt-8">
          {from === 'photo'
            ? 'Toxy compares the photo with every product in the problem stock, reads it with AI (type, colour, material, size), and shows the closest matches we already have.'
            : 'Any product works, even one we do not sell. Mention the brand or model if you know it, or just what it looks like, the size, budget and number of pieces.'}
        </EmptyState>
      )}

      {active && (
        <div className="mt-6 grid gap-6 pb-12 lg:grid-cols-[300px_minmax(0,1fr)]">
          {/* Source + controls */}
          <aside className="space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto lg:pr-1">
            <div className="overflow-hidden rounded-lg border bg-card" data-tour="sub-understood">
              <div className="flex items-center justify-between border-b px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Requested product
                <button type="button" onClick={clearSource} className="rounded p-0.5 hover:bg-accent" aria-label="Clear requested product">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {from !== 'catalog' && source ? (
                <UnderstoodCard source={source} photoUrl={from === 'photo' ? photoUrl : null} />
              ) : from !== 'catalog' && subs.error ? (
                <p className="p-3 text-sm text-destructive">{errorMessage(subs.error)}</p>
              ) : source ? (
                <div className="flex gap-3 p-3 lg:flex-col">
                  <ItemImage src={source.image_url} alt={source.name} className="aspect-square w-24 shrink-0 border p-2 lg:w-full" />
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{source.brand}</div>
                    <div className="text-sm font-medium leading-snug">{source.name}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-xs tabular-nums">
                      <Badge variant="outline">{fmtPrice(source.price)}</Badge>
                      {source.p_status && <Badge variant="muted">{source.p_status}</Badge>}
                      {(source.is_problem || pickedSource?.is_problem) && (
                        <Hint text="The requested product is itself problem stock — offer it first!">
                          <Badge variant="danger">problem stock</Badge>
                        </Hint>
                      )}
                      {source.free_stock != null && <Badge variant="muted">{fmtInt(source.free_stock)} free</Badge>}
                    </div>
                    {source.product_url && (
                      <a href={source.product_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline">
                        Open on lamptwist.com <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2 p-3">
                  <div className="aspect-square animate-pulse rounded-lg bg-muted" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                </div>
              )}
            </div>

            <div className="space-y-3 rounded-lg border bg-card p-3" data-tour="sub-controls">
              <div className="space-y-1.5">
                <div className="text-xs font-medium">Match on</div>
                <Tabs value={focus} onValueChange={(v) => setParam('focus', v === 'balanced' ? null : v)}>
                  <TabsList className="grid h-8 w-full grid-cols-4">
                    {FOCUS_OPTIONS.map((f) => (
                      <TabsTrigger key={f.value} value={f.value} className="px-1 text-xs">
                        {f.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">Price band</span>
                  <span className="tabular-nums text-muted-foreground">± {bandPct}%</span>
                </div>
                <Slider min={10} max={200} step={5} value={[bandPct]} onValueChange={([v]) => setBandPct(v)} aria-label="Price band" />
                <p className="text-[11px] text-muted-foreground">
                  {from !== 'catalog' && source && !source.estimated_price_eur
                    ? 'No price known for the request, so the price band is not used.'
                    : 'How far the shop price may deviate from the requested product.'}
                </p>
              </div>
              <label className="flex items-center justify-between gap-3 text-xs">
                <span>
                  <span className="block font-medium">Pieces needed</span>
                  <span className="text-[11px] text-muted-foreground">Favours items with enough free stock</span>
                </span>
                <Input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={qtyText}
                  onChange={(e) => setQtyText(e.target.value)}
                  placeholder={source?.pieces_needed ? String(source.pieces_needed) : 'any'}
                  className="h-8 w-20 text-right tabular-nums"
                />
              </label>
            </div>

            <div className="rounded-lg border bg-card p-3" data-tour="sub-filters">
              <FilterPanel facets={subs.data?.facets} filters={filters} onChange={setFilters} vocab={meta?.vocab} definitions={meta?.definitions} />
            </div>
          </aside>

          {/* Candidates */}
          <section className="min-w-0 space-y-4">
            {curate.data && (
              <div className="space-y-4 rounded-lg border border-violet-200 bg-violet-50/40 p-4 dark:border-violet-500/30 dark:bg-violet-500/5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                      <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-300" /> AI pick
                    </h2>
                    {curate.data.summary && <p className="mt-1 max-w-3xl text-sm leading-relaxed">{curate.data.summary}</p>}
                  </div>
                  {pickIds.length > 0 && (
                    <ShortlistPicker ids={pickIds}>
                      <Button size="sm" className="h-8 gap-1.5">
                        <ListPlus className="h-3.5 w-3.5" /> Add {pickIds.length} picks to shortlist
                      </Button>
                    </ShortlistPicker>
                  )}
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {picks.map((p) => {
                    const it = byId[p.odoo_product_id];
                    return (
                      <div key={p.odoo_product_id} className="flex gap-3 rounded-lg border bg-card p-2.5">
                        <button type="button" onClick={() => setOpenId(p.odoo_product_id)} className="shrink-0">
                          <ItemImage src={it?.image_url} className="h-20 w-20 border p-1" />
                        </button>
                        <div className="min-w-0 flex-1 space-y-1">
                          <button type="button" onClick={() => setOpenId(p.odoo_product_id)} className="block text-left">
                            <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">{it?.brand}</div>
                            <div className="line-clamp-1 text-sm font-medium hover:underline">{it?.title || `#${p.odoo_product_id}`}</div>
                          </button>
                          <p className="text-xs leading-relaxed text-muted-foreground">{p.why}</p>
                          <div className="flex items-center justify-between gap-2 text-xs tabular-nums">
                            <span className="text-muted-foreground">
                              {it ? `${fmtInt(it.free_qty)} pcs · ${fmtPrice(it.price)}` : ''}
                            </span>
                            <ShortlistPicker ids={[p.odoo_product_id]}>
                              <button type="button" className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground">
                                <ListPlus className="h-3.5 w-3.5" /> Shortlist
                              </button>
                            </ShortlistPicker>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {curate.data.customer_message && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">Message for the customer</span>
                      <CopyButton text={curate.data.customer_message} />
                    </div>
                    <pre className="whitespace-pre-wrap rounded-lg border bg-card p-3 font-sans text-sm leading-relaxed">{curate.data.customer_message}</pre>
                  </div>
                )}
                {curate.data.model && <p className="text-[11px] text-muted-foreground">Model: {curate.data.model}</p>}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold tabular-nums">{subs.data ? fmtInt(subs.data.count ?? results.length) : '…'}</span>
                <span className="text-muted-foreground">candidates from problem stock</span>
                {subs.data?.count > results.length && <span className="text-xs text-muted-foreground">(best {results.length} shown)</span>}
                {subs.isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              </div>
              <span className="text-xs text-muted-foreground">Tick up to {MAX_PICK} candidates, then let AI pick the best.</span>
            </div>
            {countFilters(filters) > 0 && (
              <div className="-mt-2 flex flex-wrap items-center gap-1.5">
                {filterChips(filters).map((c) => (
                  <button
                    key={`${c.key}:${c.value}`}
                    type="button"
                    onClick={() => setFilters(removeChip(filters, c))}
                    className="inline-flex items-center gap-1 rounded-full border bg-background px-2.5 py-0.5 text-xs hover:border-foreground/30"
                  >
                    {c.label} <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                ))}
                <button type="button" onClick={() => setFilters({})} className="text-xs text-muted-foreground underline-offset-2 hover:underline">
                  Clear filters
                </button>
              </div>
            )}

            {subs.error && <ErrorState error={subs.error} onRetry={subs.refetch} title="Could not load substitutes" />}
            {subs.isLoading && (
              <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 xl:grid-cols-3 min-[1700px]:grid-cols-4 min-[2200px]:grid-cols-5">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ItemCardSkeleton key={i} />
                ))}
              </div>
            )}
            {!subs.isLoading && !subs.error && subs.data && !results.length && (
              <EmptyState icon={PackageSearch} title="No problem stock fits">
                {countFilters(filters) > 0 ? 'Remove a filter, widen the price band or switch the match focus.' : 'Widen the price band or switch the match focus.'}
              </EmptyState>
            )}
            {results.length > 0 && (
              <div className={cn('grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 xl:grid-cols-3 min-[1700px]:grid-cols-4 min-[2200px]:grid-cols-5', subs.isFetching && 'opacity-60')}>
                {results.map((it, i) => (
                  <div key={it.odoo_product_id} className="grid" data-tour={i === 0 ? 'sub-card' : undefined}>
                    <ItemCard
                      item={it}
                      onOpen={() => setOpenId(it.odoo_product_id)}
                      selectable
                      selected={selected.includes(it.odoo_product_id)}
                      onToggleSelect={toggle}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Curate bar */}
      {active && selected.length > 0 && (
        <div className="sticky bottom-0 z-30 -mx-3 mt-6 border-t bg-background/95 shadow-[0_-4px_16px_-8px_rgba(15,23,42,0.15)] backdrop-blur sm:-mx-6">
          <div className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:px-6">
            <div className="flex shrink-0 items-center gap-2 text-sm">
              <span className="font-semibold tabular-nums">
                {selected.length}/{MAX_PICK}
              </span>
              <span className="text-muted-foreground">selected</span>
              <button type="button" onClick={() => setSelected([])} className="text-xs text-muted-foreground underline-offset-2 hover:underline">
                clear
              </button>
            </div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional constraints for the AI, e.g. warm light only, budget max €250 per piece, needs 8 pcs"
              className="min-h-[38px] flex-1 resize-none py-2"
              rows={1}
            />
            <div className="flex shrink-0 items-center gap-2">
              <ShortlistPicker ids={selected} align="end">
                <Button variant="outline" size="sm" className="h-9 gap-1.5">
                  <ListPlus className="h-3.5 w-3.5" /> Shortlist
                </Button>
              </ShortlistPicker>
              <Hint text={!caps.ai ? 'AI is not configured on the server.' : null}>
                <span>
                  <Button size="sm" className="h-9 gap-1.5" onClick={() => curate.mutate()} disabled={curate.isPending || !caps.ai}>
                    {curate.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    AI pick
                  </Button>
                </span>
              </Hint>
            </div>
          </div>
          {curate.error && <p className="px-6 pb-2 text-xs text-destructive">{errorMessage(curate.error)}</p>}
        </div>
      )}

      <ItemDetailSheet odooId={openId} onClose={() => setOpenId(null)} onOpenItem={setOpenId} />
    </div>
  );
}
